import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "bourso.theme";

/**
 * Read the current theme from the DOM. We never trust `localStorage` directly
 * because the bootstrap script in index.html may have applied an override
 * (prefers-color-scheme) before any React code ran.
 */
function readDomTheme(): Theme {
  if (typeof document === "undefined") return "light";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/**
 * useTheme — toggles a `dark` class on <html> and persists the choice.
 *
 * Default theme is light. The bootstrap script in index.html applies the
 * saved value before React mounts to prevent flashes. This hook stays in
 * sync via a state variable so components re-render on toggle.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readDomTheme());

  const setTheme = useCallback((next: Theme) => {
    const html = document.documentElement;
    if (next === "dark") html.classList.add("dark");
    else html.classList.remove("dark");
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked, only DOM is updated */
    }
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  // Cross-tab sync : if the user toggles in another tab, follow suit.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "dark" || e.newValue === "light")) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [setTheme]);

  return { theme, setTheme, toggle, isDark: theme === "dark" };
}
